import { useEffect, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { rolesApi, type RoleDetail } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { PermissionPicker } from '@/components/permission-picker';

const editSchema = z.object({
  name: z.string().trim().min(1, 'Name is required').max(100),
  description: z.string().max(500).optional(),
  permissions: z.array(z.string()),
});

type EditFormValues = z.infer<typeof editSchema>;

interface EditRoleDialogProps {
  role: RoleDetail | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function sameArray(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  const sortedA = [...a].sort();
  const sortedB = [...b].sort();
  return sortedA.every((v, i) => v === sortedB[i]);
}

export function EditRoleDialog({ role, open, onOpenChange }: EditRoleDialogProps) {
  const queryClient = useQueryClient();
  const [submitError, setSubmitError] = useState<string | null>(null);

  const { data: catalog } = useQuery({
    queryKey: ['permissions-catalog-flat'],
    queryFn: () => rolesApi.listPermissions(),
    enabled: open,
  });

  const {
    register,
    handleSubmit,
    control,
    reset,
    watch,
    formState: { errors, isDirty },
  } = useForm<EditFormValues>({
    resolver: zodResolver(editSchema),
    defaultValues: { name: '', description: '', permissions: [] },
  });

  useEffect(() => {
    if (role && open) {
      reset({
        name: role.name,
        description: role.description,
        permissions: role.permissions,
      });
      setSubmitError(null);
    }
  }, [role, open, reset]);

  const watchedPermissions = watch('permissions');
  const permissionsChanged = role ? !sameArray(role.permissions, watchedPermissions) : false;

  const mutation = useMutation({
    mutationFn: async (values: EditFormValues) => {
      if (!role) throw new Error('No role selected');

      const nameChanged = values.name !== role.name;
      const descriptionChanged = (values.description || '') !== (role.description || '');
      const permsChanged = !sameArray(role.permissions, values.permissions);

      if (!nameChanged && !descriptionChanged && !permsChanged) return role;

      return rolesApi.update(role.id, {
        name: nameChanged ? values.name : undefined,
        description: descriptionChanged ? (values.description || '') : undefined,
        permissions: permsChanged ? values.permissions : undefined,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['roles'] });
      queryClient.invalidateQueries({ queryKey: ['permissions-catalog'] });
      onOpenChange(false);
    },
    onError: (err: Error) => setSubmitError(err.message || 'Something went wrong.'),
  });

  const onSubmit = (values: EditFormValues) => {
    setSubmitError(null);
    mutation.mutate(values);
  };

  const canSubmit = isDirty || permissionsChanged;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit role</DialogTitle>
          <DialogDescription>
            {role ? <span className="font-mono text-xs">{role.key}</span> : ''}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="edit-role-name">Name</Label>
            <Input id="edit-role-name" {...register('name')} />
            {errors.name && (
              <p className="text-sm text-destructive">{errors.name.message}</p>
            )}
            <p className="text-xs text-muted-foreground">
              Renaming regenerates the <span className="font-mono">custom:</span> key.
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="edit-role-description">Description</Label>
            <Input id="edit-role-description" {...register('description')} />
          </div>

          <div className="space-y-2">
            <Label>Permissions</Label>
            <Controller
              name="permissions"
              control={control}
              render={({ field }) => (
                <PermissionPicker
                  permissions={catalog?.data || []}
                  selected={field.value}
                  onChange={field.onChange}
                  disabled={mutation.isPending}
                />
              )}
            />
          </div>

          {submitError && (
            <p className="text-sm text-destructive bg-destructive/10 border border-destructive/20 rounded-md p-2">
              {submitError}
            </p>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={mutation.isPending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={mutation.isPending || !canSubmit}>
              {mutation.isPending ? 'Saving…' : 'Save changes'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
